import React, { useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import DropdownMenu, {
	DropdownItemCheckbox,
	DropdownItemCheckboxGroup,
} from '@atlaskit/dropdown-menu';
import Button from '@atlaskit/button/new';
import Badge from '@atlaskit/badge';
import { Inline } from '@atlaskit/primitives/compiled';
import Lozenge from '@atlaskit/lozenge';
import LozengeIcon from '@atlaskit/icon-lab/core/lozenge';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';

import { LoadTimeRating } from '../ContentMetricsTable.tsx';

const RatingCheckboxItem = ({
	rating,
	isSelected,
	onClick,
	label,
	appearance,
}: {
	rating: LoadTimeRating;
	isSelected: boolean;
	onClick: (rating: LoadTimeRating) => void;
	label: string;
	appearance: 'removed' | 'moved' | 'success';
}) => {
	const handleClick = useCallback(() => onClick(rating), [onClick, rating]);
	return useMemo(
		() => (
			<DropdownItemCheckbox
				key={rating}
				id={`rating-${rating}`}
				isSelected={isSelected}
				onClick={handleClick}
			>
				<Lozenge appearance={appearance} isBold>
					{label}
				</Lozenge>
			</DropdownItemCheckbox>
		),
		[rating, isSelected, handleClick, label, appearance],
	);
};

const i18n = defineMessages({
	triggerLabel: {
		id: 'performance-insights.content-metrics.filters.rating.trigger-label.non-final',
		defaultMessage: 'Rating',
		description:
			'Label for the Rating filter dropdown trigger button in the content metrics view controls bar. Opens a dropdown to filter content by load time rating (Slow, Moderate, Fast).',
	},
	slow: {
		id: 'performance-insights.content-metrics.filters.rating.slow.non-final',
		defaultMessage: 'Slow',
		description:
			'Lozenge label in the Rating filter dropdown for the Slow load time rating category.',
	},
	moderate: {
		id: 'performance-insights.content-metrics.filters.rating.moderate.non-final',
		defaultMessage: 'Moderate',
		description:
			'Lozenge label in the Rating filter dropdown for the Moderate load time rating category.',
	},
	fast: {
		id: 'performance-insights.content-metrics.filters.rating.fast.non-final',
		defaultMessage: 'Fast',
		description:
			'Lozenge label in the Rating filter dropdown for the Fast load time rating category.',
	},
});

const RATING_LOZENGE_APPEARANCE: Record<LoadTimeRating, 'removed' | 'moved' | 'success'> = {
	[LoadTimeRating.SLOW]: 'removed',
	[LoadTimeRating.MODERATE]: 'moved',
	[LoadTimeRating.FAST]: 'success',
};

const RATING_I18N_MAP = {
	[LoadTimeRating.SLOW]: i18n.slow,
	[LoadTimeRating.MODERATE]: i18n.moderate,
	[LoadTimeRating.FAST]: i18n.fast,
};

export type RatingFilterProps = {
	selectedRatings: LoadTimeRating[];
	onSelectionChange: (selectedRatings: LoadTimeRating[]) => void;
};

export const RatingFilter = ({ selectedRatings, onSelectionChange }: RatingFilterProps) => {
	const { formatMessage } = useIntl();

	const handleClick = useCallback(
		(rating: LoadTimeRating) => {
			if (selectedRatings.includes(rating)) {
				onSelectionChange(selectedRatings.filter((r) => r !== rating));
			} else {
				onSelectionChange([...selectedRatings, rating]);
			}
		},
		[selectedRatings, onSelectionChange],
	);

	const ratings = [LoadTimeRating.SLOW, LoadTimeRating.MODERATE, LoadTimeRating.FAST];
	const hasSelections = selectedRatings.length > 0;
	const extraCount = selectedRatings.length - 1;

	const getTriggerLabel = () => {
		if (!hasSelections) {
			return formatMessage(i18n.triggerLabel);
		}
		const firstSelected = formatMessage(RATING_I18N_MAP[selectedRatings[0]]);
		return `${formatMessage(i18n.triggerLabel)}: ${firstSelected}`;
	};

	return (
		<DropdownMenu
			trigger={({ triggerRef, ...props }) => (
				<Button
					ref={triggerRef as React.Ref<HTMLButtonElement>}
					{...props}
					isSelected={hasSelections}
					iconBefore={(iconProps) => <LozengeIcon {...iconProps} label="" />}
					iconAfter={(iconProps) => <ChevronDownIcon {...iconProps} label="" />}
				>
					<Inline alignBlock="center" space="space.050">
						{getTriggerLabel()}
						{extraCount > 0 && <Badge appearance="primary">{`+${extraCount}`}</Badge>}
					</Inline>
				</Button>
			)}
			placement="bottom-start"
		>
			<DropdownItemCheckboxGroup id="rating-filter">
				{ratings.map((rating) => (
					<RatingCheckboxItem
						key={rating}
						rating={rating}
						isSelected={selectedRatings.includes(rating)}
						onClick={handleClick}
						label={formatMessage(RATING_I18N_MAP[rating])}
						appearance={RATING_LOZENGE_APPEARANCE[rating]}
					/>
				))}
			</DropdownItemCheckboxGroup>
		</DropdownMenu>
	);
};
